import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Login from './components/Auth/Login';
import PublicRoute from "./components/common/PublicRoute";
import ForgotPassword from './components/Auth/ForgotPassword';
import Profile from './components/Auth/Profile';
import Contact from './components/Auth/Contact';
import Dashboard from './components/Dashboard/Dashboard';
import Layout from './components/Layout/Layout';
import CompaniesList from './components/Company/CompaniesList';
import BranchList from './components/Branch/BranchList';
import DepartmentList from './components/Department/DepartmentList';
import DivisionList from './components/Division/DivisionList';
import EmployeeList from './components/Employee/EmployeeList';
import SurveyList from './components/Survey/SurveyList';
import SurveyCategoriesList from './components/Survey/SurveyCategoriesList';
import SurveyCategoryManager from './components/Survey/SurveyCategoryManager'; 
import SurveyCategoriesPage from './components/Survey/SurveyCategoriesPage';
import QuestionsList from './components/Survey/QuestionsList';
import SurveyLinkGenerator from './components/Survey/SurveyLinkGenerator';
import SurveyPreview from './components/Survey/SurveyPreview';
import PublicSurveyForm from './components/Survey/PublicSurveyForm';
import SendSurvey from './components/Survey/SendSurvey';
import SendReminder from './components/Survey/SendReminder';
import EmailSetup from './components/Email/EmailSetup';
import EmailTemplates from './components/Email/EmailTemplates';
import TransferSurvey from './components/Survey/TransferSurvey';
import SurveyThemes from './components/Survey/SurveyThemes';
import UsersPermissions from './components/Users/UsersPermissions';
import './index.css';
import Settings from './components/Settings/Settings';
import EmployeeSurveyReport from './components/Reports/EmployeeSurveyReport';
import SurveyResponseDashboard from './components/Survey/SurveyResponseDashboard';
import QuestionResponses from './components/Survey/QuestionResponses';
import ReportsDashboard from './components/Reports/ReportsDashboard';
import EmployeeAverageReports from './components/Reports/EmployeeAverageReports';
import ReportsCenter from './components/Reports/ReportsCenter';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
}

function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow">
        {children}
      </main>
    </div>
  );
}

function ProtectedLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Layout>
        <main className="flex-grow">
          {children}
        </main>
      </Layout>
    </div>
  );
}

function SurveyLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Layout>
        <div className="survey-layout">
          {children}
        </div>
      </Layout>
    </div>
  );
}

// Root ("/") — a logged-in user is sent on to their dashboard. A signed-out
// visitor sees the sign-in screen rendered directly at "/" (no LandingPage
// component exists anymore), so the URL stays on the domain root instead of
// the browser's address bar jumping to "/login".
function RootRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <PublicLayout>
      <Login />
    </PublicLayout>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <div className="min-h-screen bg-gray-50">
          <Routes>
            {/* Root path redirects to dashboard if logged in, otherwise to login */}
            <Route path="/" element={<RootRedirect />} />
            
            {/* Public routes */}
            <Route path="/login" element={
              <PublicLayout>
                <Login />
              </PublicLayout>
            } />
            <Route path="/forgot-password" element={
              <PublicLayout>
                <ForgotPassword />
              </PublicLayout>
            } />
            <Route path="/contact" element={
              <PublicLayout>
                <Contact />
              </PublicLayout>
            } />
            
            {/* Dashboard and Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <Dashboard />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <Profile />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/company" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <CompaniesList />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            <Route path="/branches" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <BranchList />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            <Route path="/departments" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <DepartmentList />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            <Route path="/divisions" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <DivisionList />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            <Route path="/employees" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <EmployeeList />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/surveys" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <SurveyList />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/survey-categories-manager" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <SurveyCategoryManager />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            <Route path="/survey-categories" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <SurveyCategoriesPage />
                </ProtectedLayout>
              </ProtectedRoute>
             } />

            <Route path="/surveys/:surveyId/categories" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <SurveyCategoriesList />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/survey-questions" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <QuestionsList />
                </ProtectedLayout>
              </ProtectedRoute>
            } />

            <Route path="/survey-links" element={
              <ProtectedRoute>
                <SurveyLayout>
                  <SurveyLinkGenerator />
                </SurveyLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/survey-links/:surveyId" element={
              <ProtectedRoute>
                <SurveyLayout>
                  <SurveyLinkGenerator />
                </SurveyLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/survey-preview/:surveyId" element={
              <ProtectedRoute>
                 <SurveyPreview />
              </ProtectedRoute>
            } />

           <Route path="/survey-form/:type" element={
            <PublicRoute>
              <PublicSurveyForm />
            </PublicRoute>
            } />
            
            <Route path="/send-survey" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <SendSurvey />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            <Route path="/send-reminders" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <SendReminder />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            <Route path="/survey/:surveyId/take" element={
              <PublicLayout>
                <SurveyPreview isPublic={true} />
              </PublicLayout>
            } />
            <Route path="/survey-responses" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <SurveyResponseDashboard />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            <Route path="/survey-responses/:surveyId" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <SurveyResponseDashboard />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            <Route path="/question-responses" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <QuestionResponses />
                </ProtectedLayout>
              </ProtectedRoute>
            } />

          <Route path="/question-responses/:surveyId" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <QuestionResponses />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
            <Route path="/email-setup" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <EmailSetup />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            <Route path="/email-templates" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <EmailTemplates />
                </ProtectedLayout>
              </ProtectedRoute>
            } />
            <Route path="/transfer-survey" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <TransferSurvey />
                </ProtectedLayout>
              </ProtectedRoute>
            } />  
            <Route path="/users-permissions" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <UsersPermissions />
                </ProtectedLayout>
              </ProtectedRoute>
            } />  
            <Route path="/themes" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <SurveyThemes />
                </ProtectedLayout>
              </ProtectedRoute>
            } />  
            <Route path="/settings" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <Settings />
                </ProtectedLayout>
              </ProtectedRoute>
            } />  
            <Route path="/reports/employee-surveys" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <EmployeeSurveyReport />
                </ProtectedLayout>
              </ProtectedRoute>
            } />  
            <Route path="/reports/by-reports" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <ReportsDashboard />
                </ProtectedLayout>
              </ProtectedRoute>
            } />  
            <Route path="/reports/center" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <ReportsCenter />
                </ProtectedLayout>
              </ProtectedRoute>
            } />  
            <Route path="/reports/employee-average" element={
              <ProtectedRoute>
                <ProtectedLayout>
                  <EmployeeAverageReports />
                </ProtectedLayout>
              </ProtectedRoute>
            } />  
            
            {/* 404 Fallback */}
            <Route path="*" element={
              <PublicLayout>
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
                    <p className="text-gray-600 mb-8">Page not found</p>
                    <Navigate to="/" replace />
                  </div>
                </div>
              </PublicLayout>
            } />
          </Routes>
          </div>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;